import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const GTM_ID = process.env.REACT_APP_GTM_ID || "";
// 2026-06 — SINGLE canonical pixel (1597380708389459, per Events Manager),
// env-set only via REACT_APP_META_PIXEL_ID. The old hardcoded fallback
// dataset id ("8536…501") plus a second hardcoded init in
// public/index.html made every fbq('track')
// fire against TWO pixel ids, splitting ad data. No fallback: missing
// env ⇒ pixel simply doesn't boot (fail silent, never fail split).
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";

function loadScript(src, opts = {}) {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    Object.entries(opts).forEach(([k, v]) => (s[k] = v));
    document.head.appendChild(s);
    return s;
}

// ── Perf (2026-06): two-phase analytics boot ────────────────────────
// Phase 1 (eager, ~free): install the native QUEUE STUBS (gtag dataLayer
// + fbq queue) and queue init/config. Every event fired from anywhere —
// page_view, PageView, bookfirst_*, Meta Lead/Schedule (incl. eventID) —
// buffers in the libraries' own queues, so NOTHING is lost.
// Phase 2 (deferred to browser idle OR first interaction, whichever is
// first): inject the heavy vendor scripts (gtag.js ~250ms, fbevents.js
// ~720ms main-thread). They drain the queues on load, so PageView and any
// buffered events still fire — just after first paint instead of during it.

function installStubs() {
    window.dataLayer = window.dataLayer || [];
    if (GA4_ID && !window.gtag) {
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag("js", new Date());
        window.gtag("config", GA4_ID, { send_page_view: false });
    }
    if (META_PIXEL_ID && !window.fbq) {
        const n = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        window.fbq = n;
        if (!window._fbq) window._fbq = n;
        n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
        window.fbq("init", META_PIXEL_ID);
    }
}

// F6 (2026-09) — Meta pixel script only (subset of loadVendorScripts).
// Eager-loaded on /go/* paid-landing routes: the idle/first-input deferral
// meant users who bounced in <3s never sent PageView, starving Meta's
// optimisation. Other vendors stay deferred.
function loadMetaPixelScript() {
    if (!META_PIXEL_ID || window.__fbqInit) return;
    window.__fbqInit = true;
    loadScript("https://connect.facebook.net/en_US/fbevents.js");
    if (!document.getElementById("bmi-meta-noscript")) {
        const ns = document.createElement("noscript");
        ns.id = "bmi-meta-noscript";
        ns.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1"/>`;
        document.body.appendChild(ns);
    }
}

function loadVendorScripts() {
    if (window.__analyticsVendorsLoaded) return;
    window.__analyticsVendorsLoaded = true;
    try {
        if (GA4_ID && !window.__ga4Init) {
            window.__ga4Init = true;
            loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
        }
        if (GTM_ID && !window.__gtmInit) {
            window.__gtmInit = true;
            window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
            loadScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
            const ns = document.createElement("noscript");
            ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
            document.body.insertBefore(ns, document.body.firstChild);
        }
        if (META_PIXEL_ID && !window.__fbqInit) {
            loadMetaPixelScript();
        }
        // index.html exposes deferred PostHog/Clarity boots on the same
        // idle contract — trigger them together so one idle pass boots all.
        if (typeof window.__bmiBootDeferredAnalytics === "function") {
            window.__bmiBootDeferredAnalytics();
        }
    } catch { /* analytics must never crash the app */ }
}

function scheduleVendorLoad() {
    if (window.__analyticsVendorsLoaded || window.__analyticsScheduled) return;
    window.__analyticsScheduled = true;
    const boot = () => {
        events.forEach((e) => window.removeEventListener(e, boot));
        loadVendorScripts();
    };
    const events = ["pointerdown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, boot, { once: true, passive: true }));
    if ("requestIdleCallback" in window) {
        window.requestIdleCallback(boot, { timeout: 3500 });
    } else {
        setTimeout(boot, 2500);
    }
}

/**
 * Analytics provider. Mount once inside the app root; boots GA4/GTM/Meta Pixel
 * conditionally based on env vars, then fires SPA-safe page_view events on
 * every route change.
 */
export default function AnalyticsProvider({ children }) {
    const location = useLocation();
    const bootedRef = useRef(false);
    // Last URL we fired a page_view for. Prevents duplicate hits on
    // StrictMode double-invoke (dev) or unexpected re-renders (prod).
    const lastPageViewUrlRef = useRef(null);

    useEffect(() => {
        if (bootedRef.current) return;
        bootedRef.current = true;
        try {
            installStubs();        // queues everything from t=0 — no event loss
            // F6 (2026-09) — paid landing (/go/*): load the Meta pixel
            // script EAGERLY so short-bounce sessions still send PageView.
            if (window.location.pathname.startsWith("/go/")) {
                loadMetaPixelScript();
            }
            scheduleVendorLoad();  // heavy scripts land at idle / first input
        } catch (e) {
            // Never let analytics crash the app.
            // eslint-disable-next-line no-console
            console.warn("Analytics init failed", e);
        }
    }, []);

    // Page-view tracking on SPA route change
    useEffect(() => {
        const url = location.pathname + location.search;
        if (lastPageViewUrlRef.current === url) return;   // dedupe
        lastPageViewUrlRef.current = url;
        try {
            if (GA4_ID && window.gtag) {
                window.gtag("event", "page_view", { page_path: url });
            }
            if (META_PIXEL_ID && window.fbq) {
                window.fbq("track", "PageView");
            }
            if (GTM_ID && window.dataLayer) {
                window.dataLayer.push({ event: "spa_page_view", page_path: url });
            }
        } catch { /* silent */ }
    }, [location.pathname, location.search]);

    return children;
}

/**
 * Fire a custom conversion event. Safe to call whether analytics is booted or not.
 * Pushes to GA4, GTM dataLayer, and Meta Pixel (as a custom event).
 */
export function trackEvent(name, params = {}) {
    try {
        // Ensure dataLayer always exists so events are captured even when
        // no GA4/GTM/Meta env vars are set (e.g. in preview envs).
        window.dataLayer = window.dataLayer || [];
        if (window.gtag) window.gtag("event", name, params);
        window.dataLayer.push({ event: name, ...params });
        if (window.fbq) window.fbq("trackCustom", name, params);
        // F7 (2026-09) — mirror to PostHog (same name + params). The
        // index.html shim buffers captures fired before the SDK boots,
        // so nothing is lost on early bounces. 100% of paid traffic is
        // on book-first; PostHog was blind to bookfirst_* until now.
        if (window.posthog && typeof window.posthog.capture === "function") {
            window.posthog.capture(name, params);
        }
    } catch { /* silent */ }
}

/**
 * Iter55.2 — Fire a Meta Pixel STANDARD event (CompleteRegistration, Lead,
 * SubmitApplication, Schedule, etc.) exactly once per funnel step. Uses a
 * localStorage guard on `dedupeKey` so re-renders + tab reloads never double-fire.
 * Also emits an `eventID` so we can dedupe against a future Conversions-API
 * server-side call for the same conversion.
 *
 * @param {string} event      A Meta STANDARD event name (case-sensitive).
 * @param {string} dedupeKey  Stable per-funnel-step key, e.g. `${appId}_Lead`.
 * @param {object} params     Optional Meta params (content_name, content_ids, value, currency…).
 * @returns {boolean}         true if the event actually fired; false if guarded.
 */
export function trackMetaStandard(event, dedupeKey, params = {}) {
    if (!event || !dedupeKey) return false;
    const storageKey = `bmi_meta_${event}_${dedupeKey}`;
    try {
        if (localStorage.getItem(storageKey)) return false;
    } catch { /* silent — SSR / private mode */ }
    try {
        if (typeof window !== "undefined" && window.fbq) {
            window.fbq("track", event, params, { eventID: dedupeKey });
        }
        localStorage.setItem(storageKey, new Date().toISOString());
        return true;
    } catch {
        return false;
    }
}
